import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useEffect, useMemo, useRef, useState } from "react";
import * as LucideIcons from "lucide-react";
import LucideIcon from "./LucideIcon";

// Suggested icons shown when the search box is empty so users have a sensible
// starting point. Once they type, we search across every icon lucide-react ships.
const SUGGESTED_ICONS: string[] = [
  "AppWindow",
  "LayoutGrid",
  "LayoutDashboard",
  "Boxes",
  "Package",
  "Briefcase",
  "Building2",
  "Globe",
  "Compass",
  "Star",
  "Heart",
  "Bell",
  "Mail",
  "MessageCircle",
  "Send",
  "Inbox",
  "Phone",
  "Users",
  "Calendar",
  "Clock",
  "Search",
  "Settings",
  "Wrench",
  "Key",
  "Lock",
  "ShieldCheck",
  "Database",
  "Server",
  "Cloud",
  "FileText",
  "Folder",
  "BookOpen",
  "Pencil",
  "Image",
  "Camera",
  "Video",
  "Music",
  "Headphones",
  "BarChart3",
  "LineChart",
  "PieChart",
  "TrendingUp",
  "Activity",
  "Zap",
  "Target",
  "Trophy",
  "ShoppingCart",
  "CreditCard",
  "DollarSign",
  "Tag",
  "Truck",
  "Bot",
  "Code",
  "Terminal",
  "Github",
  "Slack",
  "Linkedin",
  "Link",
  "ExternalLink",
  "Rocket",
  "Sparkles",
  "Lightbulb",
  "Sun",
  "Moon",
  "Coffee",
  "Home",
  "Layers",
  "Workflow",
];

// Icon names from lucide-react come in two forms: canonical PascalCase names
// (e.g. "AppWindow") and aliases like "AppWindowIcon" / "LucideAppWindow".
// We want a deduplicated list of canonical names for the picker.
const isCanonicalIconName = (name: string): boolean => {
  if (!/^[A-Z]/.test(name)) return false;
  if (name.endsWith("Icon")) return false;
  if (name.startsWith("Lucide")) return false;
  // Filter out non-icon helper exports (createLucideIcon, Icon, IconNode, etc.)
  if (name === "Icon" || name === "IconNode") return false;
  return true;
};

const ALL_ICON_NAMES: string[] = (() => {
  const seen = new Set<string>();
  for (const key of Object.keys(LucideIcons)) {
    if (!isCanonicalIconName(key)) continue;
    const value = (LucideIcons as Record<string, unknown>)[key];
    if (typeof value !== "function" && typeof value !== "object") continue;
    seen.add(key);
  }
  return Array.from(seen).sort((a, b) => a.localeCompare(b));
})();

const SEARCH_RESULT_LIMIT = 300;

interface IconPickerDialogProps {
  open: boolean;
  value?: string;
  onClose: () => void;
  onSelect: (iconName: string) => void;
}

const IconPickerDialog: React.FC<IconPickerDialogProps> = ({
  open,
  value,
  onClose,
  onSelect,
}) => {
  const [search, setSearch] = useState("");
  const debounceRef = useRef<number | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
    }
    debounceRef.current = window.setTimeout(() => {
      setDebouncedSearch(search.trim().toLowerCase());
    }, 120);
    return () => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
    };
  }, [search]);

  useEffect(() => {
    if (open) {
      setSearch("");
      setDebouncedSearch("");
    }
  }, [open]);

  const { results, totalMatches, suggesting } = useMemo(() => {
    if (!debouncedSearch) {
      return {
        results: SUGGESTED_ICONS,
        totalMatches: SUGGESTED_ICONS.length,
        suggesting: true,
      };
    }
    const matches = ALL_ICON_NAMES.filter((name) =>
      name.toLowerCase().includes(debouncedSearch)
    );
    return {
      results: matches.slice(0, SEARCH_RESULT_LIMIT),
      totalMatches: matches.length,
      suggesting: false,
    };
  }, [debouncedSearch]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        Choose an icon
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          fullWidth
          autoFocus
          placeholder={`Search ${ALL_ICON_NAMES.length.toLocaleString()} lucide icons...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ mb: 1.5 }}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 1.5 }}
        >
          {suggesting ? (
            <>Showing suggested icons. Start typing to search the full set.</>
          ) : results.length < totalMatches ? (
            <>
              Showing {results.length} of {totalMatches} matches. Refine your
              search to narrow it down.
            </>
          ) : (
            <>
              {totalMatches} match{totalMatches === 1 ? "" : "es"}.
            </>
          )}
        </Typography>
        {results.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
            No icons match "{search}"
          </Typography>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(72px, 1fr))",
              gap: 1,
              maxHeight: 460,
              overflowY: "auto",
            }}
          >
            {results.map((name) => {
              const isSelected = name === value;
              return (
                <Tooltip key={name} title={name} arrow>
                  <Box
                    onClick={() => {
                      onSelect(name);
                      onClose();
                    }}
                    sx={{
                      cursor: "pointer",
                      borderRadius: 1,
                      p: 1.5,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.5,
                      backgroundColor: isSelected
                        ? "action.selected"
                        : "transparent",
                      border: 1,
                      borderColor: isSelected ? "primary.main" : "divider",
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    <LucideIcon name={name} size={24} />
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default IconPickerDialog;
